/**
 * The two anonymous surfaces, as the product.
 *
 * `/c/:code` and `/claim/:token` are the screens a visitor and a recipient actually see, and until
 * now they were the only part of the application still rendered by the Phase-4 components rather
 * than by the prototype's own. These are the prototype's — the same `PublicCredentialScreen`,
 * `VerifyScreen` and `ClaimScreen` the visual acceptance measures against the frozen reference —
 * reading the real public projection instead of the demo fixture.
 *
 * Three things this is careful about.
 *
 * No session is consulted, ever. The API client below is built here, anonymously, rather than
 * taken from `useSession`: a visitor must never meet a login screen, an operator's session must
 * never leak onto a public page, and a bearer token must never be attached to a public route.
 *
 * The server decides, the page reports. `/c/:code` renders the projection's own `status`, and the
 * claim page renders the verdict `previewClaim` already reached — mapped onto the state and token
 * facts `claimGate` reads, so the page a recipient lands on and the answer their click gets still
 * come from one decision (`claim-flow.ts`).
 *
 * The raw claim token stays in the address and in the requests that must carry it. It is never
 * written to storage, never put in a query string, never logged, and never kept in state that
 * outlives the screen.
 */
import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ApiClient, readEnvConfig } from '../api/client';
import {
  getClaimArtifactSvg,
  getPublicArtifactSvg,
  getPublicCredential,
  previewClaim,
} from '../api/publicApi';
import type { ClaimPreview, PublicView } from '../api/types';
import { V07CommandsProvider } from './data/commands-context';
import { publicCommands } from './data/commands-public';
import {
  claimCredentialFromApi,
  type PublicArtifacts,
  publicCredentialFromApi,
  publicDatabase,
} from './data/public-from-api';
import { type DemoStore, V07StoreProvider } from './data/store';
import type { DemoCredential, DemoDatabase } from './data/types';
import { PublicCredentialScreen } from './screens/PublicCredentialScreen';
import { ClaimScreen, VerifyScreen } from './screens/VerifyAndClaimScreens';
import { ToastProvider } from './ui/toast';

/**
 * One anonymous client for the whole public surface.
 *
 * `getAccessToken` answers `null` unconditionally — not «read the session and maybe attach it».
 * Every public call also passes `anonymous: true`, so there are two independent reasons no
 * Authorization header can reach these routes.
 */
function useAnonymousApi(): ApiClient {
  return useMemo(
    () => new ApiClient({ baseUrl: readEnvConfig().apiBaseUrl, getAccessToken: () => null }),
    [],
  );
}

/** The store the prototype's screens read, holding the single record this page is about. */
function useOneRecordStore(credential: DemoCredential | null): {
  store: DemoStore;
  patch: (mutate: (db: DemoDatabase) => void) => void;
} {
  const [db, setDb] = useState<DemoDatabase>(() => publicDatabase(credential));
  useEffect(() => {
    setDb(publicDatabase(credential));
  }, [credential]);
  const update = useCallback((mutate: (next: DemoDatabase) => void) => {
    setDb((current) => {
      const next = { ...current, credentials: current.credentials.map((c) => ({ ...c })) };
      mutate(next);
      return next;
    });
  }, []);
  const store = useMemo<DemoStore>(
    () => ({ db, update, reset: () => setDb(publicDatabase(credential)) }),
    [db, update, credential],
  );
  return { store, patch: update };
}

/**
 * The public shell around a screen: the store, the commands and the toast host.
 *
 * Nothing renders until the fetch settles. A public page that flashed «bulunamadı» for one frame
 * before its record arrived would be telling a visitor their credential does not exist.
 */
function PublicFrame({
  credential,
  loading,
  children,
  onAccepted,
}: {
  credential: DemoCredential | null;
  loading: boolean;
  children: ReactNode;
  onAccepted?: (publicCode: string) => void;
}): ReactElement | null {
  const api = useAnonymousApi();
  const { store, patch } = useOneRecordStore(credential);
  const commands = useMemo(
    () => publicCommands({ api, patchLocal: patch, onAccepted }),
    [api, patch, onAccepted],
  );
  if (loading) return null;
  return (
    <V07StoreProvider store={store}>
      <V07CommandsProvider commands={commands}>
        <ToastProvider>{children}</ToastProvider>
      </V07CommandsProvider>
    </V07StoreProvider>
  );
}

/**
 * `/c/:code` — the canonical verification page (BACKEND §18).
 *
 * A 404 and a 409 are answers, not failures: the screen has its own «bulunamadı» and «bu kod
 * birden fazla kayda çözülüyor» branches and reaches them by finding no record, which is what an
 * empty store produces. Any other failure is left as an empty store too, because a verification
 * page that invented a record would be worse than one that says it has none.
 */
export function V07PublicCredentialRoute(): ReactElement {
  const api = useAnonymousApi();
  const params = useParams();
  const { pathname } = useLocation();
  const fromPath = /\/c\/([^/?#]+)/.exec(pathname);
  const code = params.code ?? (fromPath?.[1] ? decodeURIComponent(fromPath[1]) : '');
  const [credential, setCredential] = useState<DemoCredential | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void (async () => {
      let view: PublicView | null = null;
      try {
        view = await getPublicCredential(api, code);
      } catch {
        view = null;
      }
      let artifacts: PublicArtifacts = null;
      /* Rule 10: the drawing is the stored artifact, fetched only where the projection says the
         release permits it — the same gate the artifact route itself applies. */
      if (view && view.kind !== 'pending' && view.presentation.artifacts.svg) {
        /*
         * One request per SEALED language, and the projection says which those are.
         *
         * A single-language record asks once, exactly as before. A record sealed in two asks twice,
         * on the same canonical address with `?lang=`, so the page can answer the language switch
         * from documents it already holds — and a language whose request fails stays absent rather
         * than being filled in with the other one's picture.
         */
        const languages = view.presentation.sealedLanguages ?? [
          view.presentation.recordLanguage ?? view.presentation.defaultLanguage,
        ];
        const fetched: Partial<Record<'tr' | 'en', string | null>> = {};
        for (const lang of languages) {
          try {
            fetched[lang] = await getPublicArtifactSvg(api, view.publicCode, lang);
          } catch {
            fetched[lang] = null;
          }
        }
        artifacts = fetched;
      }
      if (!live) return;
      setCredential(view ? publicCredentialFromApi(view, artifacts) : null);
      setLoading(false);
    })();
    return () => {
      live = false;
    };
  }, [api, code]);

  return (
    <PublicFrame credential={credential} loading={loading}>
      <PublicCredentialScreen />
    </PublicFrame>
  );
}

/** `/verify` — one field and a button. It reads nothing, so it needs no store of its own. */
export function V07VerifyRoute(): ReactElement {
  return (
    <PublicFrame credential={null} loading={false}>
      <VerifyScreen />
    </PublicFrame>
  );
}

/**
 * `/claim/:token` — the recipient's one-time link (BACKEND §26).
 *
 * The token is read from the address and handed to the two calls that must carry it. It is not
 * stored, and the record built from the preview carries no raw token at all — `acceptClaim` is
 * given the address's token directly by the command layer's caller.
 */
export function V07ClaimRoute(): ReactElement {
  const api = useAnonymousApi();
  const params = useParams();
  const { pathname } = useLocation();
  const fromPath = /\/claim\/([^/?#]+)/.exec(pathname);
  const token = params.token ?? (fromPath?.[1] ? decodeURIComponent(fromPath[1]) : '');
  const [credential, setCredential] = useState<DemoCredential | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void (async () => {
      let preview: ClaimPreview | null = null;
      try {
        preview = await previewClaim(api, token);
      } catch {
        /* An unrecognised token is a 404 and it is an ANSWER — the screen's «bulunamadı» branch.
           Anything else is left as no record, which lands on the same branch rather than on a
           page that claims to know something about a link it could not read. */
        preview = null;
      }
      let artifact: string | null = null;
      let byLocale: Partial<Record<'tr' | 'en', string | null>> | undefined;
      if (preview?.status === 'claimable') {
        try {
          /* Addressed with the token this page already holds: the preview response deliberately
             does not echo the token back, not even inside a URL. */
          artifact = await getClaimArtifactSvg(api, token);
        } catch {
          artifact = null;
        }
        /*
         * ONE ENTRY PER SEALED LANGUAGE — INCLUDING WHEN THERE IS ONLY ONE.
         *
         * The claim screen has a language switch, and before this it fetched one document and showed
         * it under both tabs. Asking per language fixed that for a record sealed in two; a record
         * sealed in ONE was still wrong, because the map was built only above a count of one and the
         * screen's guard turns on by the map's presence — so a Turkish-only record went on showing its
         * Turkish certificate under the English tab. The count is not the question. The question is
         * which languages this record HAS a document in, and the answer is the same shape either way:
         * one entry per sealed language, and no key at all for a language it does not seal.
         *
         * `sealedLanguages` absent is an older server, and the only language such a response states
         * is the record's own (`lang`) — so that is the one language assumed, never a second one.
         *
         * The record's own language reuses the document fetched above rather than asking twice: that
         * request is the one this link has always made, and the route answers it from the revision's
         * own manifest, which is its own language by definition. Any other sealed language is asked
         * for by name, and one whose request fails stays ABSENT rather than being filled in with the
         * other language's picture.
         */
        const own: 'tr' | 'en' = preview.credential.lang === 'en' ? 'en' : 'tr';
        const fetched: Partial<Record<'tr' | 'en', string | null>> = {};
        for (const lang of preview.credential.sealedLanguages ?? [own]) {
          if (lang === own && artifact !== null) {
            fetched[lang] = artifact;
            continue;
          }
          try {
            fetched[lang] = await getClaimArtifactSvg(api, token, lang);
          } catch {
            fetched[lang] = null;
          }
        }
        byLocale = fetched;
      }
      if (!live) return;
      setCredential(preview ? claimCredentialFromApi(preview, artifact, byLocale) : null);
      setLoading(false);
    })();
    return () => {
      live = false;
    };
  }, [api, token]);

  return (
    <PublicFrame credential={credential} loading={loading}>
      {/* The server resolved the token; the screen renders its verdict rather than re-deriving it
          from a raw token this surface deliberately does not keep. */}
      <ClaimScreen credential={credential} />
    </PublicFrame>
  );
}
